import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  Card,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import GarageOutlinedIcon from '@mui/icons-material/GarageOutlined';
import { BlockSkeleton, EmptyState, LicensePlate, StatusChip } from '../../../components/ui';
import { Appointment, WorkshopFloor as Floor } from '../../../types';
import { fontFamilyMono, radius } from '../../../theme/tokens';
import { bayLabel, initialsOf, mechanicNameOf, taskProgress, vehicleOf } from './helpers';

interface WorkshopFloorProps {
  floor: Floor | null;
  loading: boolean;
  /** True while the socket is connected, so "Live" is never a false claim. */
  live: boolean;
  onAssign: (bay: number) => void;
  onRelease: (job: Appointment) => void;
}

const bayTagSx = {
  fontFamily: fontFamilyMono,
  fontSize: '0.71875rem',
  fontWeight: 600,
  color: 'text.secondary',
  letterSpacing: '0.02em',
} as const;

const BusyBay: React.FC<{ job: Appointment; onRelease: (job: Appointment) => void }> = ({ job, onRelease }) => {
  const progress = taskProgress(job);
  const mechanic = mechanicNameOf(job);

  return (
    <Box
      sx={{
        p: 1.75,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        borderRadius: `${radius.md}px`,
        border: 1,
        borderColor: 'divider',
        bgcolor: (t) => (t.palette.mode === 'light' ? t.palette.grey[50] : alpha(t.palette.grey[800], 0.4)),
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography sx={bayTagSx}>{bayLabel(job.bay!)}</Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <StatusChip status={job.status} />
          <Tooltip title="Take out of bay">
            <IconButton size="small" aria-label={`Take the ${vehicleOf(job)} out of ${bayLabel(job.bay!)}`} onClick={() => onRelease(job)}>
              <LogoutOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontWeight: 650, fontSize: '0.90625rem' }}>
          {vehicleOf(job)}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
          {job.licensePlate && <LicensePlate plate={job.licensePlate} />}
          <Typography variant="body2" color="text.secondary" noWrap>
            {job.serviceDescription}
          </Typography>
        </Stack>
      </Box>

      <LinearProgress
        variant="determinate"
        value={progress.percent}
        aria-label={`${progress.done} of ${progress.total} tasks done`}
        sx={{ height: 6, borderRadius: `${radius.pill}px` }}
      />

      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Stack direction="row" alignItems="center" spacing={0.875} sx={{ minWidth: 0 }}>
          <Avatar
            sx={{
              width: 22,
              height: 22,
              fontSize: '0.625rem',
              fontWeight: 700,
              bgcolor: 'background.paper',
              color: 'text.secondary',
              border: 1,
              borderColor: 'divider',
            }}
          >
            {mechanic ? initialsOf(mechanic) : '–'}
          </Avatar>
          <Typography variant="caption" color="text.secondary" noWrap>
            {mechanic ?? 'No mechanic'}
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
          {progress.total ? `${progress.done} of ${progress.total} tasks` : 'No tasks'}
        </Typography>
      </Stack>
    </Box>
  );
};

const EmptyBay: React.FC<{ bay: number; onAssign: (bay: number) => void }> = ({ bay, onAssign }) => (
  <ButtonBase
    onClick={() => onAssign(bay)}
    aria-label={`Assign a job to ${bayLabel(bay)}`}
    sx={{
      minHeight: 148,
      p: 1.75,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.75,
      borderRadius: `${radius.md}px`,
      border: '1.5px dashed',
      borderColor: 'divider',
      color: 'text.secondary',
      transition: (t) => t.transitions.create(['border-color', 'color']),
      '&:hover, &.Mui-focusVisible': { borderColor: 'primary.main', color: 'primary.main' },
    }}
  >
    <Typography sx={{ ...bayTagSx, color: 'inherit' }}>{bayLabel(bay)}</Typography>
    <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.primary' }}>Available</Typography>
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <AddIcon sx={{ fontSize: 16 }} />
      <Typography variant="body2" sx={{ fontWeight: 600, color: 'inherit' }}>
        Assign a job
      </Typography>
    </Stack>
  </ButtonBase>
);

/** The bays and what is in each — the live half of the dashboard. */
const WorkshopFloor: React.FC<WorkshopFloorProps> = ({ floor, loading, live, onAssign, onRelease }) => {
  const busy = floor?.jobs.length ?? 0;
  const bayCount = floor?.bayCount ?? 0;
  const byBay = new Map((floor?.jobs ?? []).map((job) => [job.bay!, job]));
  // Jobs in a bay the shop has since removed still show, after the real bays.
  const extraBays = [...byBay.keys()].filter((bay) => bay > bayCount).sort((a, b) => a - b);

  return (
    <Card sx={{ p: 2.5, height: '100%' }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" component="h2" sx={{ fontSize: '1rem' }}>
            Workshop floor
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {bayCount ? `${busy} of ${bayCount} bays busy` : 'Live bay status'}
          </Typography>
        </Box>
        {live && (
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ color: 'success.dark' }}>
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: 'success.main',
                boxShadow: (t) => `0 0 0 3px ${t.palette.success.light}`,
              }}
            />
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'inherit' }}>
              Live
            </Typography>
          </Stack>
        )}
      </Stack>

      {loading && !floor ? (
        <BlockSkeleton lines={6} />
      ) : bayCount === 0 && busy === 0 ? (
        <EmptyState
          dense
          icon={<GarageOutlinedIcon />}
          title="Set up your bays"
          description="Tell MechaniX how many bays you run, then drop jobs into them from here."
          action={
            <Button component={RouterLink} to="/admin/shop-settings" variant="outlined" size="small">
              Add bays in settings
            </Button>
          }
        />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          }}
        >
          {[...Array.from({ length: bayCount }, (_, i) => i + 1), ...extraBays].map((bay) => {
            const job = byBay.get(bay);
            return job ? (
              <BusyBay key={bay} job={job} onRelease={onRelease} />
            ) : (
              <EmptyBay key={bay} bay={bay} onAssign={onAssign} />
            );
          })}
        </Box>
      )}
    </Card>
  );
};

export default WorkshopFloor;
